import cls from 'classnames';
import { useCallback, useRef, useEffect } from 'react';
import { Button, Overlay, Popover, CloseButton, Form } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import { EventPriority } from 'src/components/Trap/Elements';
import { useEffectState } from 'src/utils/hooks';

import classnames from './BuildingMapsSidePanel.module.scss';

export const BuildingMapsSidePanel = ({ items, selectedItem, editMode, onEditMode, onSelectItem, onDropItem, currentMapId, isValidBuildingPosition, mapOptions = [], onSelectMap, canToggleEditMode = false, canEditGroupMaps = false, groupId, availableGroupIds }) => {
  const listRef = useRef();
  const headerRef = useRef();
  const showPopover = useRef(true);
  const [popover, setPopover] = useEffectState(() => canToggleEditMode && editMode, [editMode, canToggleEditMode]);
  const history = useHistory();

  const handleMapChange = useCallback((event) => {
    const nextId = Number(event.target.value);

    if (Number.isNaN(nextId) || !onSelectMap) {
      return;
    }

    onSelectMap(nextId);
  }, [onSelectMap]);

  useEffect(() => { listRef.current && selectedItem && listRef.current.scrollToItem(selectedItem.index) }, [selectedItem, listRef.current]);

  useEffect(() => {
    if (!canToggleEditMode) {
      showPopover.current = false;
      return;
    }
    showPopover.current = true;
  }, [canToggleEditMode]);

  function handleHidePopover() {
    showPopover.current = false;
    setPopover(false);
  }

  return (
    <div className="w-100 h-100 d-flex flex-column flex-nowrap" style={{ minHeight: 0 }}>
      {canToggleEditMode && (
        <div ref={headerRef} className='d-flex flex-row gap-col-3 px-3 pt-2 pb-3 justify-content-end align-items-center'>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted mb-0 mt-2">{__('Edit mode')}</span>
            <Form.Check
              type="switch"
              id="building-maps-edit-switch"
              className="fs-4 m-0 align-self-center"
              checked={editMode}
              onChange={() => onEditMode(!editMode)}
              aria-label={__('Toggle edit mode')}
            />
          </div>
        </div>
      )}
      <div className="border-bottom" />

      <div className="w-100 flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList ref={listRef} height={height} width={width} itemCount={items.length} itemSize={49}>
              {(({ index, style }) => {
                const item = items[index];
                const isSelected = selectedItem && Number(item.trap_id) === Number(selectedItem.trap_id);
                return (
                  <ItemRow item={item} isSelected={isSelected} editMode={editMode}
                    onSelect={onSelectItem} onDrop={onDropItem} style={style} currentMapId={currentMapId}
                    isValidBuildingPosition={isValidBuildingPosition} onNavigate={history.push}
                    groupId={groupId} mapOptions={mapOptions} canEditGroupMaps={canEditGroupMaps}
                    availableGroupIds={availableGroupIds}
                  />
                );
              })}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>

      {canToggleEditMode && (
        <Overlay target={headerRef.current} show={popover && showPopover.current} placement='bottom' rootClose onHide={handleHidePopover} >
          {(props) => (
            <Popover id="popover-basic" {...props} className="edit-mode-popover">
              <Popover.Header><CloseButton onClick={handleHidePopover} /></Popover.Header>
              <Popover.Body>{__('In order to place a trap onto a map, click on Arrow Down icon next to the trap name in the list above. A needle indicating trap position will then be placed in the middle of the map, and you can now drag and drop the needle.')}</Popover.Body>
            </Popover>
          )}
        </Overlay>
      )}

    </div >
  )
}

const ItemRow = ({ item, isSelected, editMode, onSelect, onDrop, style, currentMapId, isValidBuildingPosition, onNavigate, groupId, mapOptions = [], canEditGroupMaps = false, availableGroupIds }) => {
  const { trap_name, event_priority, is_caught, event_name, changed } = item;

  const handleItemClick = useCallback(() => onSelect(item), [item, onSelect]);
  const handleDropClick = useCallback(() => editMode && onDrop(item), [item, editMode, onDrop]);
  const handleNavigate = useCallback((event, href) => {
    event.preventDefault();
    event.stopPropagation();
    if (href && typeof onNavigate === 'function') {
      onNavigate(href);
    }
  }, [onNavigate]);

  const savedMapId = Number.isFinite(item?.position?.map_id) ? Number(item.position.map_id) : null;
  const savedMapGroupId = Number.isFinite(item?.mapGroupId) ? Number(item.mapGroupId) : null;
  const isOnCurrentGroup = savedMapGroupId === Number(groupId);
  const mapExists = Array.isArray(mapOptions) && mapOptions.some(({ id }) => Number(id) === savedMapId);
  const groupIsAvailable = availableGroupIds instanceof Set ? availableGroupIds.has(savedMapGroupId) : true;
  const showBuildingLinkSameGroup = Number.isFinite(savedMapId) && savedMapId !== Number(currentMapId) && Number.isFinite(Number(groupId)) && isOnCurrentGroup && mapExists;
  const showBuildingLinkDifferentGroup = Number.isFinite(savedMapId) && Number.isFinite(savedMapGroupId) && !isOnCurrentGroup && groupIsAvailable;
  const showBuildingLink = showBuildingLinkSameGroup || showBuildingLinkDifferentGroup;
  const showGoogleLink = !Number.isFinite(savedMapId) && item?.hasGoogleCoordinates === true;
  const targetGroupId = showBuildingLinkDifferentGroup ? savedMapGroupId : groupId;
  const linkHref = showBuildingLink
    ? `/groups/${Number(targetGroupId)}/maps/${savedMapId}/traps?trap_id=${item.trap_id}`
    : (showGoogleLink ? `/map?trap_id=${item.trap_id}` : null);

  return (
    <div className={cls(classnames.item, { [classnames.active]: isSelected })} style={style} onClick={handleItemClick} >
      <span className={classnames.icon}>
        <EventPriority
          priority={event_priority}
          isCaught={is_caught}
          eventName={event_name}
        />
      </span>
      <span className={classnames.text}>
        <span className={classnames.name} title={trap_name}>{trap_name}</span>
        {item?.serial_no && (
          <div className={cls(classnames.serial, "text-muted")} title={item.serial_no}>{item.serial_no}</div>
        )}
      </span>
      <div className="ms-2 me-2 d-inline-flex align-items-center justify-content-center">
        {linkHref ? (
          <a
            href={linkHref}
            className="d-inline-flex align-items-center justify-content-center text-muted"
            onClick={(e) => handleNavigate(e, linkHref)}
            title={showBuildingLink ? __('View on another building map') : __('View on Google Maps')}
            aria-label={showBuildingLink ? __('View on another building map') : __('View on Google Maps')}
            style={{ textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#495057'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
          >
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
          </a>
        ) : (
          <span className="d-inline-flex align-items-center justify-content-center invisible">
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
          </span>
        )}
      </div>
      {canEditGroupMaps && !isValidBuildingPosition(changed, currentMapId) && (
        <div className="me-2 d-inline-flex align-items-center justify-content-center">
          <div className={cls(classnames.location, { [classnames.active]: editMode })} onClick={handleDropClick} />
        </div>
      )}
    </div>
  )
}
